<template>
  <div
    class="cfg-card"
  >
    <div class="cfg-card-header">
      <span class="cfg-card-title">{{ $t('configurator.offer_lines') }}</span>
      <div class="d-flex align-items-center">
        <b-button
          variant="outline-secondary"
          class="cfg-btn mr-75"
          @click="toggleAll"
        >
          <feather-icon
            :icon="allExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'"
            size="14"
          />
          {{ allExpanded ? $t('configurator.hide_technical_data') : $t('configurator.show_technical_data') }}
        </b-button>
        <span class="cfg-badge cfg-badge-gray">{{ $tc('configurator.lines_count', rows.length, { count: rows.length }) }}</span>
      </div>
    </div>
    <div class="summary-table-wrap">
      <table class="summary-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>{{ $t('configurator.product') }}</th>
            <th>{{ $t('configurator.measures') }}</th>
            <th>{{ $t('configurator.units_short') }}</th>
            <th>{{ $t('configurator.motor') }}</th>
            <th>{{ $t('configurator.box_short') }}</th>
            <th>{{ $t('configurator.extras') }}</th>
            <th class="text-right">
              {{ $t('configurator.unit_price') }}
            </th>
            <th class="text-right">
              {{ $t('configurator.total') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in rows">
            <tr
              :key="row.line.id"
              class="curtain-row"
              :class="{ 'validation-row': row.attr.price_pending, open: isExpanded(row) }"
              @click="toggleRow(row)"
            >
              <td class="text-nowrap">
                <feather-icon
                  :icon="isExpanded(row) ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                  size="13"
                  class="row-chevron"
                />
                <span class="cfg-badge cfg-badge-primary font-weight-bolder">{{ row.line.type }}</span>
              </td>
              <td>
                <div class="font-weight-bold">
                  {{ row.ref }}
                </div>
                <div class="cfg-hint">
                  {{ tr(row.line.product_object.name) }}
                </div>
              </td>
              <td class="text-nowrap">
                {{ row.attr.l1 }} × {{ row.attr.h1 }} mm
                <div
                  v-if="row.attr.cajon || row.attr.guias"
                  class="cfg-hint"
                >
                  <span v-if="row.attr.cajon">{{ $t('configurator.drawer') }} {{ row.attr.cajon }}</span>
                  <span v-if="row.attr.cajon && row.attr.guias"> · </span>
                  <span v-if="row.attr.guias">{{ $t('configurator.side_guides') }} {{ row.attr.guias }}</span>
                </div>
              </td>
              <td>{{ row.line.quantity }}</td>
              <td>
                <span
                  v-if="row.attr.position"
                  class="cfg-badge cfg-badge-gray"
                >{{ row.attr.position }}</span>
                <span v-else>—</span>
              </td>
              <td>
                <template v-if="row.box">
                  <span class="cfg-badge cfg-badge-green">{{ row.box.line.type }} · {{ row.box.line.product_object.ref }}</span>
                  <span class="cfg-badge cfg-badge-gray ml-25">{{ tr(row.box.attr.modelo) }}</span>
                </template>
                <span
                  v-else-if="row.needsBox"
                  class="cfg-badge cfg-badge-orange"
                >{{ $t('configurator.unassigned') }}</span>
                <span
                  v-else-if="row.noBox"
                  class="cfg-badge cfg-badge-gray"
                >{{ $t('configurator.no_box') }}</span>
                <span v-else>—</span>
              </td>
              <td>
                <div
                  v-for="extra in row.extras"
                  :key="extra.line.id"
                  class="extra-line"
                >
                  {{ tr(extra.line.product_object.name) }}<span v-if="extra.attr.ral"> · RAL {{ extra.attr.ral }}</span><span v-else-if="extra.attr.comments"> · {{ extra.attr.comments }}</span>
                  <price-input
                    v-if="priceEditable && manualPrice(extra)"
                    :line="extra.line"
                    class="extra-price"
                    @save="setPrice"
                  />
                  <span
                    v-else-if="extra.attr.price_pending"
                    class="cfg-badge cfg-badge-orange extra-price"
                  >{{ $t('configurator.price_pending') }}</span>
                  <span
                    v-else
                    class="extra-price"
                  >· {{ money(extra.line.amount) }}</span>
                </div>
                <span
                  v-if="!row.extras.length"
                  class="cfg-hint"
                >—</span>
              </td>
              <td class="col-amount">
                <price-input
                  v-if="priceEditable && manualPrice(row)"
                  :line="row.line"
                  @save="setPrice"
                />
                <span
                  v-else-if="row.attr.price_pending"
                  class="cfg-badge cfg-badge-orange"
                >{{ $t('configurator.price_pending') }}</span>
                <span v-else>{{ money(curtainAmount(row.line)) }}</span>
              </td>
              <td class="col-total">
                <span v-if="!row.attr.price_pending">{{ money(rowTotal(row)) }}</span>
                <span
                  v-else
                  class="cfg-hint"
                >—</span>
              </td>
            </tr>
            <tr
              v-if="isExpanded(row)"
              :key="`${row.line.id}-detail`"
              class="detail-row"
            >
              <td colspan="9">
                <div class="detail-title">
                  {{ $t('configurator.technical_data') }} · {{ row.line.type }}
                </div>
                <div class="detail-grid">
                  <div class="tech-row">
                    <span>{{ $t('configurator.length') }} (L1)</span><strong>{{ row.attr.l1 || '—' }} mm</strong>
                  </div>
                  <div class="tech-row">
                    <span>{{ $t('configurator.height') }} (H1)</span><strong>{{ row.attr.h1 || '—' }} mm</strong>
                  </div>
                  <div class="tech-row">
                    <span>{{ $t('configurator.length') }} (L2)</span><strong>{{ row.attr.l2 || '—' }} mm</strong>
                  </div>
                  <div class="tech-row">
                    <span>{{ $t('configurator.height') }} (H2)</span><strong>{{ row.attr.h2 || '—' }} mm</strong>
                  </div>
                  <div class="tech-row">
                    <span>{{ $t('configurator.drawer') }}</span><strong>{{ row.attr.cajon || '—' }}</strong>
                  </div>
                  <div class="tech-row">
                    <span>{{ $t('configurator.side_guides') }}</span><strong>{{ guidesText(row) }}</strong>
                  </div>
                  <div class="tech-row">
                    <span>{{ $t('configurator.motor') }}</span><strong>{{ row.attr.motor ? `${row.attr.motor} ×${row.attr.num_motor}` : '—' }}</strong>
                  </div>
                  <div class="tech-row">
                    <span>{{ $t('configurator.motor_position') }}</span><strong>{{ positionText(row.attr.position) }}</strong>
                  </div>
                </div>
                <div
                  v-if="row.attr.notes"
                  class="detail-notes"
                >
                  <strong>{{ $t('configurator.technical_notes') }}:</strong> {{ row.attr.notes }}
                </div>
              </td>
            </tr>
          </template>
          <tr
            v-for="box in boxRows"
            :key="box.line.id"
            class="sub-row"
          >
            <td>
              <span class="cfg-badge cfg-badge-green">{{ box.line.type }}</span>
            </td>
            <td colspan="6">
              <span class="font-weight-bold">{{ box.line.product_object.ref }}</span>
              · {{ tr(box.attr.modelo) }}
              <span class="cfg-hint">({{ box.attr.cortinas.map(c => c.type).join(', ') }})</span>
              <span
                v-if="box.attr.per_curtain"
                class="cfg-hint"
              >· {{ $t('configurator.includes_per_curtain', { quantity: box.attr.per_curtain.quantity, ref: box.attr.per_curtain.ref }) }}</span>
            </td>
            <td class="col-amount">
              <price-input
                v-if="priceEditable && manualPrice(box)"
                :line="box.line"
                @save="setPrice"
              />
              <span
                v-else-if="box.attr.price_pending"
                class="cfg-badge cfg-badge-orange"
              >{{ $t('configurator.price_pending') }}</span>
              <span v-else>{{ money(box.line.amount) }}</span>
            </td>
            <td class="col-total">
              <span v-if="!box.attr.price_pending">{{ money(box.line.amount) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { BButton } from 'bootstrap-vue'
import {
  curtainAmount, money, offerBoxes, offerRows, translate,
} from '../offer'
import PriceInput from './PriceInput.vue'

// Tabla de líneas de la oferta: resumen del configurador, vista y revisión del pedido
export default {
  components: {
    BButton, PriceInput,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    // Estado del configurador ({ items, noBox }); null en ofertas del tarificador anterior
    state: {
      type: Object,
      default: null,
    },
    // Revisión de Tecnitex: se puede poner precio a las líneas pendientes
    priceEditable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expanded: [],
    }
  },
  computed: {
    ...mapGetters({
      lang: 'languages/getCurrentLanguage',
    }),
    rows() {
      return offerRows(this.order, this.state)
    },
    boxRows() {
      return offerBoxes(this.order)
    },
    allExpanded() {
      return this.rows.length > 0 && this.rows.every(row => this.expanded.includes(row.line.id))
    },
  },
  methods: {
    money,
    tr(value) {
      return translate(value, this.lang)
    },
    curtainAmount(line) {
      return curtainAmount(this.order, line)
    },
    rowTotal(row) {
      return this.curtainAmount(row.line) * row.line.quantity
        + row.extras.reduce((total, e) => total + Number(e.line.amount) * e.line.quantity, 0)
    },
    isExpanded(row) {
      return this.expanded.includes(row.line.id)
    },
    toggleRow(row) {
      this.expanded = this.isExpanded(row)
        ? this.expanded.filter(id => id !== row.line.id)
        : [...this.expanded, row.line.id]
    },
    toggleAll() {
      this.expanded = this.allExpanded ? [] : this.rows.map(row => row.line.id)
    },
    guidesText(row) {
      if (row.attr.guias) return row.attr.guias
      const guide = row.extras.find(e => this.tr(e.line.product_object.name).startsWith('SG-'))
      if (guide) return `${this.tr(guide.line.product_object.name)} (${this.$t('configurator.extras').toLowerCase()})`
      return '—'
    },
    positionText(position) {
      if (position === 'Izquierda') return this.$t('configurator.left')
      if (position === 'Derecha') return this.$t('configurator.right')
      return '—'
    },
    // Líneas en Validación OT o con precio puesto a mano por Tecnitex (se puede corregir)
    manualPrice(entry) {
      return !!(entry.attr.price_pending || entry.attr.manual_price)
    },
    setPrice(payload) {
      this.$emit('set-price', payload)
    },
  },
}
</script>

<style lang="scss" scoped>
.summary-table-wrap {
  overflow-x: auto;
}
.summary-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: .8125rem;
  th {
    padding: .625rem .875rem;
    border-bottom: 1px solid #e0e0e0;
    background: #f6f6f6;
    font-size: .7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .5px;
    color: #6e6b7b;
    white-space: nowrap;
  }
  td {
    padding: .75rem .875rem;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: middle;
    color: #4d4d4d;
  }
  tr:last-child td {
    border-bottom: none;
  }
  tr:hover td {
    background: #f6f6f6;
  }
  tr.sub-row td {
    padding-top: .4rem;
    padding-bottom: .4rem;
    background: #f6f6f6;
  }
  tr.validation-row td {
    background: #fffbeb;
  }
  .col-amount {
    text-align: right;
    font-weight: 700;
    color: #1e1e1e;
    white-space: nowrap;
  }
  .col-total {
    text-align: right;
    font-weight: 800;
    color: #ac1a19;
    white-space: nowrap;
  }
}
.curtain-row {
  cursor: pointer;
  &.open td {
    background: #fffaf5;
  }
}
.row-chevron {
  margin-right: .25rem;
  color: #b8c2cc;
}
.summary-table tr.detail-row td {
  padding: .75rem 1.25rem 1rem 2.5rem;
  background: #fffaf5;
  border-bottom: 1px solid #e0e0e0;
}
.detail-title {
  margin-bottom: .5rem;
  font-size: .7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .6px;
  color: #82868b;
}
.detail-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  column-gap: 1.5rem;
  @media (max-width: 991px) {
    grid-template-columns: repeat(2, 1fr);
  }
}
.tech-row {
  display: flex;
  justify-content: space-between;
  padding: .35rem 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: .8rem;
  span {
    color: #6e6b7b;
  }
  strong {
    color: #1e1e1e;
  }
}
.detail-notes {
  margin-top: .6rem;
  font-size: .8rem;
  color: #4d4d4d;
}
.extra-line {
  display: block;
  font-size: .75rem;
  color: #4d4d4d;
  & + & {
    margin-top: .2rem;
  }
}
.extra-price {
  margin-left: .4rem;
  font-weight: 600;
  color: #ac1a19;
  white-space: nowrap;
}
</style>
